import React, { useState } from 'react'
import {
	Button,
	Modal,
	Space,
	Form,
	Input,
	Checkbox,
	Upload,
	message,
	Tooltip
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { useSmartTranslation } from '../../cls/TranslationContext'
/**
 * PrettyJsonButton component provides a button to prettify JSON in a form textarea.
 * It reads the current value of the form’s `textarea` field, parses it as JSON,
 * pretty-prints it with 4-space indentation, writes it back into the form,
 * shows a success or error message, and invokes an optional callback with the parsed object.
 *
 * @component
 * @param {Object} props.form – AntD Form instance, used to get/set field values.
 * @param {Function} [props.onSuccess] – Callback invoked with the parsed object on successful prettify.
 * @param {string} [props.label] – Text label for the button.
 * @returns {JSX.Element}
 */
const PrettyJsonButton = ({ form, onSuccess, label = 'Prettify JSON' }) => {
	const [loading, setLoading] = useState(false)
	/**
	 * handlePrettifyJson parses and prettifies the JSON in the form’s `textarea` field.
	 * - Retrieves the raw JSON string from `form.getFieldsValue().textarea`.
	 * - Attempts to parse it; on success, formats it with `JSON.stringify(obj, null, 4)`.
	 * - Updates the form field, shows a message, and calls `onSuccess(parsedObject)` if provided.
	 * - On parse error, displays an “Invalid JSON object.” error message.
	 *
	 * @function
	 * @private
	 */
	const handlePrettifyJson = () => {
		const values = form.getFieldsValue()
		const textarea = values.textarea

		try {
			setLoading(true)
			const parsedObject = JSON.parse(textarea)
			const pretty = JSON.stringify(parsedObject, null, 4)
			form.setFieldsValue({ textarea: pretty })
			message.success('JSON prettified successfully!')

			if (typeof onSuccess === 'function') {
				onSuccess(parsedObject)
			}
		} catch (error) {
			message.error('Invalid JSON object.')
		} finally {
			setLoading(false)
		}
	}

	return (
		<Space style={{ width: '100%' }}>
			<Tooltip title="Prettify the JSON content in the textarea">
				<Button
					type="dashed"
					onClick={handlePrettifyJson}
					loading={loading}
					style={{ width: '100%' }}
				>
					{label}
				</Button>
			</Tooltip>
		</Space>
	)
}

PrettyJsonButton.propTypes = {
	form: PropTypes.object.isRequired,
	onSuccess: PropTypes.func,
	label: PropTypes.string
}
/**
 * ImportExport component offers import/export controls for form data as JSON.
 * - **Export**: serializes all form fields, builds a downloadable `.json` file and triggers download.
 * - **Import**: opens a modal to paste JSON or upload a file, lets the user pick which keys to import,
 *   and writes them back into the form.
 *
 * @component
 * @param {Object} props.form – AntD Form instance to read and write values.
 * @param {Function} [props.withValuesExecute] – Callback with imported values after confirmation.
 * @returns {JSX.Element}
 */
const ImportExport = ({ form, withValuesExecute }) => {
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [importMode, setImportMode] = useState(null) // 'textarea' or 'file'
	const [importedObject, setImportedObject] = useState({})
	const [selectedKeys, setSelectedKeys] = useState([])
	const [textareaForm] = Form.useForm()
	const { t } = useSmartTranslation()
	/**
	 * handleExport serializes the current form values to JSON and triggers a file download.
	 * - Sanitizes the filename based on `form.getFieldsValue().name` or defaults to `exported_file.json`.
	 * - Creates a Blob from the JSON string, generates a URL, clicks a hidden `<a>` to download,
	 *   then revokes the URL.
	 *
	 * @function
	 * @private
	 */
	const handleExport = () => {
		const values = form.getFieldsValue()
		const name = values.name
			? values.name
					.toLowerCase()
					.replace(/\s/g, '_')
					.replace('/', '_')
					.replace(/[^a-z0-9_]/g, '')
			: 'exported_file'
		const fileName = name || 'form_data'
		const json = JSON.stringify(values)
		const blob = new Blob([json], { type: 'application/json' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `${fileName}.json`
		a.click()
		URL.revokeObjectURL(url)
	}
	/**
	 * handleImportClick opens the import modal in the specified mode.
	 * - `mode` can be `'textarea'` or `'file'`.
	 * - Resets any previously imported data and selections.
	 *
	 * @function
	 * @param {'textarea'|'file'} mode – Import mode to activate.
	 * @private
	 */
	const handleImportClick = (mode) => {
		setImportMode(mode)
		setImportedObject({})
		setSelectedKeys([])
		textareaForm.resetFields()
		setIsModalVisible(true)
	}
	/**
	 * handleTextareaImport parses a raw JSON string from the textarea import form.
	 * - Tries to `JSON.parse(values)`.
	 * - On success, stores the parsed object in state and selects all its keys.
	 * - On failure, displays an “Invalid JSON object.” error message.
	 *
	 * @function
	 * @param {string} values – The raw JSON string from the textarea.
	 * @private
	 */
	const handleTextareaImport = (values) => {
		try {
			const parsedObject = JSON.parse(values)
			setImportedObject(parsedObject)
			setSelectedKeys(Object.keys(parsedObject))
		} catch (error) {
			message.error('Invalid JSON object.')
		}
	}
	/**
	 * handleFileImport reads and parses an uploaded `.json` file.
	 * - Uses a `FileReader` to read the file text.
	 * - Attempts `JSON.parse`; on success, stores the object, selects its keys,
	 *   switches to file-import mode, reopens the modal, and shows a success message.
	 * - On failure, shows an “Invalid JSON file.” error.
	 * - Returns `false` to prevent AntD’s automatic upload.
	 *
	 * @function
	 * @param {File} file – The uploaded JSON file.
	 * @returns {boolean}
	 * @private
	 */
	const handleFileImport = (file) => {
		const reader = new FileReader()
		reader.onload = (e) => {
			try {
				const parsedObject = JSON.parse(e.target.result)
				setImportedObject(parsedObject)
				setSelectedKeys(Object.keys(parsedObject))
				setImportMode('file')
				setIsModalVisible(true)
				message.success(
					'File imported successfully. Please review and confirm.'
				)
			} catch (error) {
				message.error('Invalid JSON file.')
			}
		}
		reader.readAsText(file)
		return false // Prevent Ant Design upload from auto-uploading the file
	}
	/**
	 * handleKeySelection updates which keys the user has checked for import.
	 *
	 * @function
	 * @param {string[]} keys – Array of JSON object keys selected.
	 * @private
	 */
	const handleKeySelection = (keys) => {
		setSelectedKeys(keys)
	}
	/**
	 * confirmImport filters the imported object down to the user-selected keys,
	 * sets those values into the main form via `form.setFieldsValue()`,
	 * invokes `withValuesExecute(filteredObject)` if provided, closes the modal,
	 * and displays a success message.
	 *
	 * @function
	 * @private
	 */
	const confirmImport = () => {
		const filteredObject = selectedKeys.reduce((acc, key) => {
			acc[key] = importedObject[key]
			return acc
		}, {})
		form.setFieldsValue(filteredObject)
		withValuesExecute && withValuesExecute(filteredObject)
		setIsModalVisible(false)
		message.success('Form values updated successfully.')
	}

	return (
		<div>
			<Space style={{ width: '100%' }}>
				<Button
					type="default"
					onClick={() => handleImportClick('textarea')}
				>
					{t('importExport.textarea')}
				</Button>
				<Upload
					beforeUpload={handleFileImport}
					accept=".json"
					showUploadList={false}
				>
					<Button icon={<UploadOutlined />}>
						{t('importExport.file')}
					</Button>
				</Upload>
				<Button type="default" onClick={handleExport}>
					Export
				</Button>
			</Space>

			<Modal
				title={
					importMode === 'textarea'
						? t('importExport.importByTextarea')
						: t('importExport.importByFile')
				}
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				onOk={confirmImport}
				okText="Import Selected"
				cancelText="Cancel"
			>
				{importMode === 'textarea' && (
					<Form
						form={textareaForm}
						layout="vertical"
						onFinish={({ textarea }) =>
							handleTextareaImport(textarea)
						}
					>
						<Form.Item
							name="textarea"
							label={t('importExport.jsonObject')}
							rules={[
								{
									required: true,
									message: t(
										'single.invalid.json'
									)
								}
							]}
						>
							<Input.TextArea
								rows={6}
								placeholder={t('importExport.pasteJson')}
							/>
						</Form.Item>

						<PrettyJsonButton
							form={textareaForm}
							onSuccess={() =>
								handleTextareaImport(
									textareaForm.getFieldValue('textarea')
								)
							}
							label={t('importExport.parse')}
						/>
					</Form>
				)}

				{importMode === 'file' && (
					<Space style={{ width: '100%' }} direction="vertical">
						<p>{t('importExport.reviewImported')}</p>
						<Checkbox.Group
							options={Object.keys(importedObject)}
							value={selectedKeys}
							onChange={handleKeySelection}
						/>
						<Space>
							<Button
								onClick={() =>
									setSelectedKeys(Object.keys(importedObject))
								}
							>
								{t('single.select-all')}
							</Button>
							<Button onClick={() => setSelectedKeys([])}>
								{t('single.deselect-all')}
							</Button>
							<Button
								onClick={() =>
									setSelectedKeys(
										Object.keys(importedObject).filter(
											(key) => !selectedKeys.includes(key)
										)
									)
								}
							>
								{t('importExport.invertSelection')}
							</Button>
						</Space>
					</Space>
				)}
			</Modal>
		</div>
	)
}

ImportExport.propTypes = {
	form: PropTypes.object.isRequired,
	withValuesExecute: PropTypes.func
}

export default ImportExport
